import { createContext, type ReactNode, useMemo, useReducer } from "react";
import { createReducer, type Dispatch } from "src/utils/create-reducer";

export type ImageCarouselState = {
  selectedImageIndex: number;
  selectedZoomImageIndex: number;
  updateIdentifier: "regular" | "zoom" | null;
  isImageZoomed: boolean;
};

export type ImageCarouselActions =
  | {
      type: "SET_SELECTED_IMAGE_INDEX";
      payload: {
        selectedImageIndex: number;
        updateIdentifier: "regular" | "zoom" | null;
      };
    }
  | {
      type: "SET_SELECTED_ZOOM_IMAGE_INDEX";
      payload: number;
    }
  | {
      type: "TOGGLE_IMAGE_ZOOMED";
      payload: boolean;
    };

export type UseImageCarouselContext = {
  imageCarouselState: ImageCarouselState;
  imageCarouselDispatch: Dispatch<ImageCarouselActions>;
};

const initialState: ImageCarouselState = {
  selectedImageIndex: 0,
  selectedZoomImageIndex: 0,
  updateIdentifier: null,
  isImageZoomed: false,
};

export const ImageCarouselContext =
  createContext<ImageCarouselState>(initialState);

export const ImageCarouselDispatchContext = createContext<
  Dispatch<ImageCarouselActions> | undefined
>(undefined);

export const imageCarouselReducer = createReducer<
  ImageCarouselState,
  ImageCarouselActions
>({
  SET_SELECTED_IMAGE_INDEX: (state, payload) => ({
    ...state,
    ...payload,
  }),
  SET_SELECTED_ZOOM_IMAGE_INDEX: (state, payload) => ({
    ...state,
    selectedZoomImageIndex: payload,
  }),
  TOGGLE_IMAGE_ZOOMED: (state) => ({
    ...state,
    isImageZoomed: !state.isImageZoomed,
  }),
});

export function ImageCarouselContextProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [imageCarouselState, imageCarouselDispatch] = useReducer(
    imageCarouselReducer,
    initialState
  );

  const imageCarouselContextValue = useMemo<ImageCarouselState>(
    () => imageCarouselState,
    [imageCarouselState]
  );

  return (
    <ImageCarouselContext.Provider value={imageCarouselContextValue}>
      <ImageCarouselDispatchContext.Provider value={imageCarouselDispatch}>
        {children}
      </ImageCarouselDispatchContext.Provider>
    </ImageCarouselContext.Provider>
  );
}
